import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PageShell from "../../components/ui/Page/PageShell";
import Button from "../../components/ui/Button/Button";
import { useAuth } from "../../hooks/useAuth";
import { feedbackApi } from "../../lib/feedback";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import type { FeedbackSubmissionRequestDto, FeedbackTypeDto } from "../../types/types";

const validFeedbackTypes: FeedbackTypeDto[] = [
  "QUESTION",
  "BUG",
  "FEATURE_IDEA",
  "GENERAL_FEEDBACK",
  "POST_COMPLETION_WEBSITE_FEEDBACK",
];

const getFeedbackType = (value: string | null): FeedbackTypeDto => (
  value && validFeedbackTypes.includes(value as FeedbackTypeDto)
    ? value as FeedbackTypeDto
    : "GENERAL_FEEDBACK"
);

const getRespondentRole = (value: string | null): FeedbackSubmissionRequestDto["respondentRole"] => (
  value === "TEEN" || value === "POSTER" ? value : null
);

const FeedbackPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const feedbackType = getFeedbackType(searchParams.get("type"));
  const gigRequestId = searchParams.get("gigRequestId");
  const respondentRole = getRespondentRole(searchParams.get("role") ?? searchParams.get("respondentRole"));
  const isCompletionFeedback = feedbackType === "POST_COMPLETION_WEBSITE_FEEDBACK";
  const sourcePath = `${location.pathname}${location.search}${location.hash}`;

  const [email, setEmail] = useState(user?.email ?? "");
  const [rating, setRating] = useState("5");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedEmail || !trimmedMessage) {
      setError("Please add your email and a short message.");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await feedbackApi.submit({
        type: feedbackType,
        email: trimmedEmail,
        message: trimmedMessage,
        sourcePath,
        gigRequestId: gigRequestId || null,
        respondentRole,
        rating: rating ? Number(rating) : null,
      });
      setSuccess(true);
    } catch (submissionError) {
      setError(apiErrorToMessage(submissionError, "We could not send your feedback right now."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell width="xl">
      <section className="rounded-2xl border border-myteengig-border bg-white p-5 shadow-sm sm:p-6">
        <div className="border-b border-myteengig-border pb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Feedback
          </p>
          <h1 className="mt-2 text-2xl font-semibold text-myteengig-text-primary">
            Tell us about MyTeenGig
          </h1>
          <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
            {isCompletionFeedback
              ? "This optional feedback is about the website experience for this completed gig."
              : "Share what worked, what felt unclear, or what would make the website better."}
          </p>
        </div>

        {success ? (
          <div className="mt-5 space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              Thanks. Your feedback was sent.
            </div>
            <div className="flex justify-end">
              <Button onClick={() => navigate(respondentRole === "POSTER" ? "/adult/reviews" : respondentRole === "TEEN" ? "/teen/reviews" : "/dashboard")}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={(event) => void handleSubmit(event)} className="mt-5 space-y-4">
            <div>
              <label htmlFor="feedback-email" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                Your email
              </label>
              <input
                id="feedback-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="feedback-rating" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                Website experience rating
              </label>
              <select
                id="feedback-rating"
                value={rating}
                onChange={(event) => setRating(event.target.value)}
                className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
              >
                <option value="5">5 - Very good</option>
                <option value="4">4 - Good</option>
                <option value="3">3 - Okay</option>
                <option value="2">2 - Difficult</option>
                <option value="1">1 - Very difficult</option>
              </select>
            </div>

            <div>
              <label htmlFor="feedback-message" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                Feedback
              </label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={6}
                placeholder="Tell us what worked well or what made the website harder to use."
                className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
              />
            </div>

            {error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send feedback"}
              </Button>
            </div>
          </form>
        )}
      </section>
    </PageShell>
  );
};

export default FeedbackPage;
